// src/hooks/useFCMToken.js
import { useEffect, useState } from 'react';
import { getFCMToken, isFCMAvailable, onForegroundMessage } from '../lib/firebase';
import { supabase } from '../lib/supabaseClient';

const closeForeground = cleanup => {
  try { cleanup?.(); } catch { console.warn('Notification listener cleanup unavailable.'); }
};

export function useFCMToken(userId) {
  const [fcmToken, setFcmToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fcmSupported, setFcmSupported] = useState(false);

  // Check FCM support on mount
  useEffect(() => {
    let active = true;
    const checkSupport = async () => {
      try {
        const supported = await isFCMAvailable();
        if (active) setFcmSupported(supported);
      } catch {
        if (active) setFcmSupported(false);
      }
    };
    
    checkSupport();
    return () => { active = false; };
  }, []);

  // Initialize FCM token when user is available and FCM is supported
  useEffect(() => {
    if (!userId || !fcmSupported) return;

    let active = true;
    let unsubscribe;
    initializeFCMToken(() => active).then(cleanup => {
      if (active) unsubscribe = cleanup;
      else closeForeground(cleanup);
    }).catch(() => {
      if (active) setError('Notifications unavailable. Please try again later.');
    });
    return () => { active = false; closeForeground(unsubscribe); };
  }, [userId, fcmSupported]);

  const initializeFCMToken = async (isCurrent = () => true) => {
    setIsLoading(true);
    setError(null);

    try {
      // Request notification permission if not already granted
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          throw new Error('Notification permission denied');
        }
      } else if (Notification.permission === 'denied') {
        throw new Error('Notification permission denied');
      }

      if (!isCurrent()) return;

      // Register the Firebase messaging service worker
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      }

      if (!isCurrent()) return;

      // Get FCM token
      const token = await getFCMToken();
      if (!isCurrent()) return;
      
      if (token) {
        setFcmToken(token);
        await saveFCMToken(token, isCurrent);
        if (!isCurrent()) return;
        
        // Set up foreground message listener
        const unsubscribe = onForegroundMessage((payload) => {
          if (!isCurrent()) return;
          
          // Show browser notification for foreground messages
          if ('serviceWorker' in navigator && typeof ServiceWorkerRegistration !== 'undefined' && 'showNotification' in ServiceWorkerRegistration.prototype) {
            navigator.serviceWorker.ready.then((registration) => {
              return registration.showNotification(
                payload.notification?.title || 'FootyCoach',
                {
                  body: payload.notification?.body || 'New notification',
                  icon: payload.notification?.icon || '/icon-192.png',
                  tag: payload.data?.tag || 'footy-coach-fcm',
                  data: payload.data || {}
                }
              );
            }).catch(() => {
              if (isCurrent()) setError('Unable to display a notification.');
            });
          }
        });

        // Return cleanup function
        return unsubscribe;
      } else {
        throw new Error('Failed to get FCM token');
      }
    } catch (err) {
      if (isCurrent()) setError('Notifications unavailable. Please try again later.');
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  };

  const saveFCMToken = async (token, isCurrent = () => true) => {
    try {
      if (!isCurrent()) return;
      
      // Save to profiles table alongside existing push_subscription
      const { error } = await supabase
        .from('profiles')
        .update({ 
          fcm_token: token,
          fcm_token_updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }
      if (!isCurrent()) return;
      
      // Also try to call the existing save-subscription function to maintain compatibility
      try {
        const { error: functionError } = await supabase.functions.invoke('save-subscription', {
          body: { 
            userId, 
            fcmToken: token,
            type: 'fcm'
          }
        });
        if (functionError) throw functionError;
      } catch (funcError) {
        if (isCurrent()) setError('Notification registration could not be completed.');
        console.warn('Notification registration service unavailable.');
      }

    } catch (err) {
      throw err;
    }
  };

  const refreshFCMToken = async () => {
    if (!fcmSupported) return;
    
    setIsLoading(true);
    try {
      const token = await getFCMToken();
      if (token && token !== fcmToken) {
        setFcmToken(token);
        await saveFCMToken(token);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFCMToken = async () => {
    try {
      await supabase
        .from('profiles')
        .update({ 
          fcm_token: null,
          fcm_token_updated_at: null
        })
        .eq('id', userId);
      
      setFcmToken(null);
    } catch (err) {
      setError(err.message);
    }
  };

  return {
    fcmToken,
    isLoading,
    error,
    fcmSupported,
    refreshFCMToken,
    clearFCMToken,
    initializeFCMToken
  };
}
